import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faEnvelope,
  faChalkboardTeacher,
  faBook,
  faChartLine,
  faCalendarAlt,
  faTrophy,
  faArrowUp,
  faArrowDown,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faUserGraduate,
  faPercentage,
  faIdCard,
  faSchool,
  faAward
} from '@fortawesome/free-solid-svg-icons';
import ApiServices from '../Services/ApiServices';
import Loader from '../Shared/Loader';
import Header from '../Shared/Header';
import Footer from '../Shared/Footer';
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const StudentInfo = () => {
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudentInfo();
  }, []);

  const fetchStudentInfo = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiServices.get('Student/Info');

      if (response.check && response.data) {
        const validatedData = {
          ...response.data,
          info: response.data.info || {
            teacher_name: response.data.teacher || 'غير محدد',
            exams_taken: 0,
            average_score: 0,
            last_exam_date: new Date().toISOString().split('T')[0],
            completion_rate: 0,
            improvement: 0
          }
        };
        setStudentData(validatedData);
      } else {
        setError(response.msg || 'فشل في تحميل بيانات الطالب');
      }
    } catch (error) {

      setError('حدث خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const getImprovementIcon = (improvement) => {
    if (improvement > 0) return faArrowUp;
    if (improvement < 0) return faArrowDown;
    return faCheckCircle;
  };

  if (loading) {
    return <Loader text="جاري تحميل ملفك الشخصي..." />;
  }

  return (
    <>
      <Header />
      <br></br>
      <section className="si-root" id="student-profile">
        <div className="si-container">
          <AnimatePresence mode="wait">
            {error ? (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="si-error-box"
              >
                <div className="si-error-card">
                  <div className="si-error-icon">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  </div>
                  <h3>حدث خطأ ما</h3>
                  <p>{error}</p>
                  <button onClick={fetchStudentInfo} className="si-retry-btn">
                    <FontAwesomeIcon icon={faSpinner} className={loading ? 'fa-spin' : ''} />
                    <span>إعادة المحاولة</span>
                  </button>
                </div>
              </motion.div>
            ) : !studentData ? (
              <div className="si-empty-state">
                <FontAwesomeIcon icon={faUserGraduate} className="si-empty-icon" />
                <h3>لا توجد بيانات متاحة</h3>
              </div>
            ) : (
              <div className="si-dashboard">
                {/* ── header ── */}
                <motion.div
                  className="si-header"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                >
                  <motion.div className="si-eyebrow" variants={fadeUp} custom={0}>
                    <span className="si-eyebrow-dot" />
                    الملف التعريفي للطالب
                  </motion.div>
                  <motion.h2 className="si-title" variants={fadeUp} custom={1}>
                    لوحة <em>المتابعة والإنجاز</em>
                  </motion.h2>
                  <motion.p className="si-subtitle" variants={fadeUp} custom={2}>
                    هنا تجد ملخصاً شاملاً لمسيرتك التعليمية، بياناتك الشخصية، ومعدلات الأداء في الاختبارات
                  </motion.p>
                </motion.div>

                {/* ── Hero Profile ── */}
                <motion.div
                  className="si-banner"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                >
                  <div className="si-banner-content">
                    <div className="si-avatar-group">
                      <div className="si-avatar">
                        <FontAwesomeIcon icon={faUserGraduate} />
                      </div>
                      <div className="si-status-dot">
                        <FontAwesomeIcon icon={faCheckCircle} />
                      </div>
                    </div>
                    <div className="si-banner-text">
                      <h1>مرحباً، {studentData.full_name}</h1>
                      <div className="si-tags">
                        <span>{studentData.user_class}</span>
                        <span className="si-tag-sep">•</span>
                        <span>{studentData.material || 'طالب منصة إمتعاني'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="si-banner-pattern" />
                </motion.div>

                {/* ── Stats Grid ── */}
                <div className="si-stats-row">
                  <StatCard
                    icon={faTrophy}
                    label="إجمالي الاختبارات"
                    value={studentData.info?.exams_taken || 0}
                    color="#6366f1"
                    delay={0.1}
                  />
                  <StatCard
                    icon={faPercentage}
                    label="معدل الدرجات"
                    value={`${studentData.info?.average_score || 0}%`}
                    color="#2563eb"
                    delay={0.2}
                  />
                  <StatCard
                    icon={faChartLine}
                    label="نسبة الإكمال"
                    value={`${studentData.info?.completion_rate || 0}%`}
                    color="#10b981"
                    delay={0.3}
                  />
                  <StatCard
                    icon={getImprovementIcon(studentData.info?.improvement || 0)}
                    label="معدل التحسن"
                    value={`${(studentData.info?.improvement || 0) > 0 ? '+' : ''}${studentData.info?.improvement || 0}%`}
                    color={(studentData.info?.improvement || 0) >= 0 ? "#10b981" : "#ef4444"}
                    delay={0.4}
                  />
                </div>

                {/* ── Details Grid ── */}
                <div className="si-grid">
                  {/* Personal Info */}
                  <motion.div
                    className="si-card"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <div className="si-card-header">
                      <div className="si-card-icon" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>
                        <FontAwesomeIcon icon={faIdCard} />
                      </div>
                      <h3>المعلومات الشخصية</h3>
                    </div>
                    <div className="si-info-list">
                      <InfoItem icon={faUser} label="اسم المستخدم" value={studentData.user_name} />
                      <InfoItem icon={faEnvelope} label="بريد ولي الأمر" value={studentData.parent_email} />
                      <InfoItem icon={faSchool} label="الصف الدراسي" value={studentData.user_class} />
                      <InfoItem
                        icon={faCalendarAlt}
                        label="تاريخ آخر اختبار"
                        value={studentData.info?.last_exam_date ? new Date(studentData.info.last_exam_date).toLocaleDateString('ar-EG') : 'لا يوجد'}
                      />
                    </div>
                    <div className="si-card-bar" style={{ background: '#6366f1' }} />
                  </motion.div>

                  {/* Academic Info */}
                  <motion.div
                    className="si-card"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <div className="si-card-header">
                      <div className="si-card-icon" style={{ background: 'rgba(232,146,10,0.1)', color: '#6366f1' }}>
                        <FontAwesomeIcon icon={faAward} />
                      </div>
                      <h3>المسار الأكاديمي</h3>
                    </div>
                    <div className="si-info-list">
                      <InfoItem icon={faChalkboardTeacher} label="المعلم المسؤول" value={studentData.teacher || 'غير محدد'} />
                      <InfoItem icon={faBook} label="المادة الأساسية" value={studentData.material || 'غير محدد'} />

                      <div className="si-progress-box">
                        <div className="si-progress-header">
                          <span>الحالة الدراسية</span>
                          <span className="si-progress-val">{studentData.info?.completion_rate || 0}%</span>
                        </div>
                        <div className="si-progress-track">
                          <motion.div
                            className="si-progress-fill"
                            initial={{ width: 0 }}
                            animate={{ width: `${studentData.info?.completion_rate || 0}%` }}
                            transition={{ duration: 1.2, delay: 0.8 }}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="si-card-bar" style={{ background: '#6366f1' }} />
                  </motion.div>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
        <Footer/>
        <style>{`
          .si-root {
            font-family: "Zain", sans-serif;
            direction: rtl;
            position: relative;
            padding: 80px 0 100px;
            background: #fff;
            overflow: hidden;
            isolation: isolate;
          }

          .si-container {
            position: relative; z-index: 2;
            max-width: 1260px; margin: 0 auto; padding: 0 40px;
          }

          /* Header */
          .si-header { text-align: center; margin-bottom: 50px; }
          .si-eyebrow {
            display: inline-flex; align-items: center; gap: 10px;
            color: #6366f1; font-weight: 800; font-size: 0.82rem;
            letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 22px;
          }
          .si-eyebrow-dot {
            width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
            animation: si-pulse 2.2s ease-in-out infinite;
          }
          @keyframes si-pulse {
            0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.45); }
            50%{ box-shadow: 0 0 0 9px rgba(232,146,10,0); }
          }
          .si-title {
            font-size: clamp(2.2rem, 4vw, 3.6rem);
            font-weight: 900; color: #0f172a;
            margin: 0 0 18px; line-height: 1.2;
          }
          .si-title em {
            font-style: normal;
            background: linear-gradient(90deg, #6366f1 0%, #6366f1 55%, #6366f1 100%);
            -webkit-background-clip: text; -webkit-text-fill-color: transparent;
            background-clip: text;
          }
          .si-subtitle {
            font-size: 1.08rem; color: #64748b; line-height: 1.85; margin: 0;
            max-width: 700px; margin: 0 auto;
          }

          /* Banner */
          .si-banner {
            position: relative;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 32px;
            padding: 50px 60px;
            margin-bottom: 40px;
            overflow: hidden;
            display: flex;
            align-items: center;
            box-shadow: 0 10px 30px rgba(0,0,0,0.03);
          }
          .si-banner-content {
            position: relative; z-index: 2;
            display: flex; align-items: center; gap: 40px;
          }
          .si-avatar-group { position: relative; }
          .si-avatar {
            width: 110px; height: 110px;
            background: #fff;
            border: 1px solid #e2e8f0;
            border-radius: 30px;
            display: flex; align-items: center; justify-content: center;
            font-size: 50px; color: #6366f1;
          }
          .si-status-dot {
            position: absolute; bottom: -5px; right: -5px;
            width: 34px; height: 34px; background: #10b981;
            border: 4px solid #fff; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-size: 14px; color: #fff;
          }
          .si-banner-text h1 {
            font-size: 2.2rem; font-weight: 900; color: #0f172a; margin: 0 0 10px;
          }
          .si-tags { display: flex; align-items: center; gap: 12px; }
          .si-tags span { color: #64748b; font-size: 1.1rem; font-weight: 800; }
          .si-tag-sep { opacity: 0.3; }
          .si-banner-pattern {
            position: absolute; top: 0; left: 0; width: 100%; height: 100%;
            background: radial-gradient(circle at 80% 20%, rgba(99,102,241,0.05) 0%, transparent 40%);
            pointer-events: none;
          }

          /* Stats */
          .si-stats-row {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 20px;
            margin-bottom: 40px;
          }
          .si-stat-card {
            background: #fff;
            border: 1px solid rgba(15,23,42,0.08);
            border-radius: 24px;
            padding: 24px;
            display: flex; align-items: center; gap: 20px;
            transition: all 0.3s;
          }
          .si-stat-card:hover {
            box-shadow: 0 12px 30px rgba(0,0,0,0.06);
            transform: translateY(-2px);
          }
          .si-stat-icon {
            width: 54px; height: 54px;
            border-radius: 16px;
            display: flex; align-items: center; justify-content: center;
            font-size: 22px;
          }
          .si-stat-info { display: flex; flex-direction: column; }
          .si-stat-val { font-size: 1.6rem; font-weight: 900; color: #0f172a; line-height: 1; }
          .si-stat-lab { font-size: 0.85rem; color: #64748b; font-weight: 700; margin-top: 6px; }

          /* Details Grid */
          .si-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 30px;
          }
          .si-card {
            background: #fff;
            border: 1px solid rgba(15,23,42,0.08);
            border-radius: 28px;
            padding: 35px;
            position: relative;
            overflow: hidden;
            box-shadow: 0 4px 20px rgba(0,0,0,0.02);
          }
          .si-card-header {
            display: flex; align-items: center; gap: 18px;
            margin-bottom: 30px;
          }
          .si-card-icon {
            width: 48px; height: 48px; border-radius: 14px;
            display: flex; align-items: center; justify-content: center; font-size: 20px;
          }
          .si-card-header h3 { font-size: 1.4rem; font-weight: 900; color: #0f172a; margin: 0; }

          .si-info-list { display: flex; flex-direction: column; gap: 24px; }
          .si-info-item { display: flex; align-items: center; gap: 18px; }
          .si-item-icon { color: #cbd5e1; font-size: 1.1rem; width: 24px; text-align: center; }
          .si-item-text { display: flex; flex-direction: column; }
          .si-item-lab { font-size: 0.8rem; color: #94a3b8; font-weight: 700; }
          .si-item-val { font-size: 1.05rem; color: #334155; font-weight: 800; }

          .si-progress-box {
            margin-top: 10px;
            padding-top: 25px;
            border-top: 1px dashed rgba(15,23,42,0.1);
          }
          .si-progress-header {
            display: flex; justify-content: space-between;
            margin-bottom: 12px; font-weight: 800; color: #475569;
          }
          .si-progress-val { color: #6366f1; }
          .si-progress-track {
            height: 10px; background: #f1f5f9; border-radius: 20px; overflow: hidden;
          }
          .si-progress-fill {
            height: 100%; background: linear-gradient(90deg, #6366f1, #6366f1);
            border-radius: 20px;
          }
          .si-card-bar {
            position: absolute; bottom: 0; left: 0; height: 4px; width: 100%;
          }

          /* States */
          .si-error-box { display: flex; justify-content: center; padding: 60px 0; }
          .si-error-card {
            background: #fff; border: 1px solid #fee2e2; border-radius: 32px;
            padding: 50px; text-align: center; max-width: 450px;
          }
          .si-error-icon {
            width: 70px; height: 70px; background: #fee2e2; color: #ef4444;
            border-radius: 50%; display: flex; align-items: center; justify-content: center;
            font-size: 30px; margin: 0 auto 20px;
          }
          .si-retry-btn {
            background: #6366f1; color: #fff; border: none;
            padding: 14px 30px; border-radius: 14px; font-weight: 800;
            display: inline-flex; align-items: center; gap: 10px;
            cursor: pointer; transition: all 0.2s; margin-top: 20px;
          }
          .si-retry-btn:hover { background: #1e293b; transform: translateY(-2px); }

          .si-empty-state { text-align: center; padding: 100px 0; color: #94a3b8; }
          .si-empty-icon { font-size: 4rem; margin-bottom: 20px; opacity: 0.3; }

          @media(max-width:1100px){
            .si-stats-row { grid-template-columns: repeat(2, 1fr); }
            .si-grid { grid-template-columns: 1fr; }
          }
          @media(max-width:768px){
            .si-root { padding: 60px 0 80px; }
            .si-container { padding: 0 20px; }
            .si-banner { padding: 40px; }
            .si-banner-content { flex-direction: column; text-align: center; }
            .si-avatar { width: 90px; height: 90px; font-size: 40px; }
            .si-banner-text h1 { font-size: 1.8rem; }
            .si-stats-row { grid-template-columns: 1fr; }
          }
        `}</style>
      </section>
    </>
  );
};

// Sub-components
const StatCard = ({ icon, label, value, color, delay }) => (
  <motion.div
    className="si-stat-card"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay }}
  >
    <div className="si-stat-icon" style={{ background: `${color}15`, color }}>
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="si-stat-info">
      <span className="si-stat-val">{value}</span>
      <span className="si-stat-lab">{label}</span>
    </div>
  </motion.div>
);

const InfoItem = ({ icon, label, value }) => (
  <div className="si-info-item">
    <div className="si-item-icon">
      <FontAwesomeIcon icon={icon} />
    </div>
    <div className="si-item-text">
      <span className="si-item-lab">{label}</span>
      <span className="si-item-val">{value}</span>
    </div>
  </div>
);

export default StudentInfo;
